import React from 'react';
import ReactDOM from 'react-dom';
import 'antd/dist/antd.min.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { setupAuthInterceptors } from './services/serviceconfig';
import { redirectUnauthenticatedDirectOpen } from './utils/authSession';

// Benign Ant Design / chart ResizeObserver noise — debounce callbacks and hide dev overlay
(function patchResizeObserverLoop() {
  if (typeof window === 'undefined' || !window.ResizeObserver) return;

  const loopRe = /ResizeObserver loop/;
  const NativeResizeObserver = window.ResizeObserver;

  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let frameId;
      super((entries, observer) => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };

  window.addEventListener('error', (event) => {
    if (loopRe.test(event.message || '')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  const nativeConsoleError = console.error.bind(console);
  console.error = (...args) => {
    const first = args[0];
    if (typeof first === 'string' && loopRe.test(first)) return;
    nativeConsoleError(...args);
  };
})();

setupAuthInterceptors();

function isAxiosNetworkError(reason) {
  if (!reason) return false;
  if (reason?.code === 'ERR_NETWORK' || reason?.code === 'ECONNABORTED') {
    return true;
  }
  const message = String(reason?.message || '').toLowerCase();
  if (message.includes('network error')) {
    return true;
  }
  return Boolean(reason?.isAxiosError && !reason?.response);
}

function isSuppressedRuntimeError(reason) {
  if (!reason) return false;
  const requestUrl = String(reason?.config?.url || '');
  const method = String(reason?.config?.method || '').toLowerCase();
  const isLoginAttempt = requestUrl.includes('/login/') && method === 'post';
  const isLogoutAttempt = requestUrl.includes('/logout/') && method === 'post';
  const message = String(reason?.message || reason || '');
  const isUnauthorized = reason?.response?.status === 401
    || message.includes('status code 401');
  const isForbidden = reason?.response?.status === 403
    || message.includes('status code 403');
  const isNotFound = reason?.response?.status === 404
    || message.includes('status code 404');

  return Boolean(
    isLoginAttempt
    || isLogoutAttempt
    || isAxiosNetworkError(reason)
    || reason?.__authHandled
    || isUnauthorized
    || isForbidden
    || isNotFound
  );
}

function suppressDevOverlay(event) {
  event.preventDefault();
  event.stopImmediatePropagation();
}

// Capture phase so webpack-dev-server overlay never paints for auth/network noise
window.addEventListener('unhandledrejection', (event) => {
  if (isSuppressedRuntimeError(event.reason)) {
    suppressDevOverlay(event);
  }
}, true);

window.addEventListener('error', (event) => {
  const message = String(event.message || '');
  const candidate = event.error || event.message;
  if (
    isSuppressedRuntimeError(candidate)
    || message === 'Script error.'
    || message === 'Script error'
    || /ResizeObserver loop/.test(message)
    || message.includes('status code 401')
    || message.includes('status code 403')
    || message.includes('status code 404')
  ) {
    suppressDevOverlay(event);
  }
}, true);

if (!redirectUnauthenticatedDirectOpen()) {
  ReactDOM.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
    document.getElementById('root')
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
